import type { AxiosError } from "axios";
import axios, {
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import type { ErrorEnvelope } from "../api/envelope";
import type { AuthUser } from "../types/auth-types/AuthUser";
import {
  TOKEN_STORAGE_KEY,
  USER_STORAGE_KEY,
} from "../config/auth-storage-keys";
import { AUTH_COOKIE_SESSION } from "../config/auth-features";
import { webCommonHost } from "../config/host";
import { authStorage } from "../config/session-storage";
import {
  readStoredWorkFor,
  workForHeaderValues,
  WORK_FOR_STORAGE_KEY,
} from "../utils/work-for-storage";
import { refreshCognitoIdToken } from "./cognito-auth";
import { isIssuerIdTokenExpired } from "./issuer-claims";
import { endSession, isLoggingOut, storedIssuerIdToken } from "./oidc-auth";
import { beginSilentReauthIfExpired } from "./silent-reauth";
import { OIDC_ENABLED } from "../config/auth-features";
import { broadcastLogout } from "../utils/auth-sync";
import {
  API_BASE_URL as RESOLVED_API_BASE_URL,
  CSRF_HEADER,
  readCsrfCookie,
  setCsrfToken,
} from "./api-session";
import { refreshSession, stopSessionRefresh } from "./session-refresh";

declare module "axios" {
  // Opt a request into forwarding the end-user's platform issuer id_token as
  // `x-pem-user-authorization` (see the request interceptor). Set only on the
  // self-profile read/write calls.
  export interface AxiosRequestConfig {
    forwardUserToken?: boolean;
  }
}

export const API_BASE_URL = RESOLVED_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error(
    "API config missing — set VITE_API_BASE_URL for this deployment.",
  );
}

const Api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15_000,
  // Required for the `penn_rt` / `penn_csrf` cookies to travel to the sibling
  // API host at all. Business routes do not authenticate from the cookie —
  // `penn_rt` is `Path=/v1/auth`-scoped and the browser never attaches it to
  // them — but `/v1/auth/*` calls made through this instance need it.
  withCredentials: true,
});

Api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // The Cognito → Penn JWT exchange ships with `x-skip-penn-token: 1` so a
  // stale Penn token doesn't override the body's Cognito token. Strip it
  // here and skip the auth header attach.
  const skipPenn = config.headers.get?.("x-skip-penn-token");
  if (skipPenn) {
    config.headers.delete("x-skip-penn-token");
  } else {
    const token = authStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
  }

  // Calls flagged `forwardUserToken` additionally carry the end-user's platform
  // issuer id_token in `x-pem-user-authorization`. svc-03 relays it to the
  // platform registration-api, which reads/writes the shared demographic subset
  // in the central directory (the SigV4 Lattice hop owns `Authorization`, so the
  // user token rides in this separate header). Scoped to the self-profile
  // read/write so the token is not attached to every request. No stored issuer
  // token (a Cognito-mode sign-in) leaves the header off: the read path falls
  // back to local columns, the write path refuses the shared edit.
  if (config.forwardUserToken) {
    const idToken = storedIssuerIdToken();
    if (idToken) {
      config.headers.set("x-pem-user-authorization", `Bearer ${idToken}`);
    }
  }

  const rawUser = authStorage.getItem(USER_STORAGE_KEY);
  if (rawUser) {
    try {
      const user = JSON.parse(rawUser) as { id?: string; isInternal?: boolean };
      // Tag internal users so internal-only upstreams (e.g. svc-09 similarity
      // matches, spec §6) accept the request. The gateway forwards this header
      // verbatim; deriving it from the JWT at the gateway is a future hardening
      // step (it does not yet decode the token).
      config.headers.delete("x-auth-scope");
      if (user?.isInternal === true) {
        config.headers.set("x-auth-scope", "internal");
      }
      const selfId = user?.id;
      if (selfId) {
        const stored = readStoredWorkFor();
        const wf = workForHeaderValues(stored, selfId);
        config.headers.delete("x-penn-work-for-user-id");
        config.headers.delete("x-penn-work-for-email");
        if (wf.userId) {
          config.headers.set("x-penn-work-for-user-id", wf.userId);
        } else if (wf.email) {
          config.headers.set("x-penn-work-for-email", wf.email);
        }
      }
    } catch {
      authStorage.removeItem(WORK_FOR_STORAGE_KEY);
    }
  }

  // Double-submit CSRF echo, on the two cookie-authenticated routes only.
  // Sending it everywhere would be harmless but misleading — it would suggest
  // business routes depend on it, when their CSRF-immunity comes from using a
  // Bearer token held in memory.
  const path = config.url ?? "";
  if (
    AUTH_COOKIE_SESSION &&
    (path.includes("/v1/auth/refresh") || path.includes("/v1/auth/logout"))
  ) {
    const csrf = readCsrfCookie();
    if (csrf) config.headers.set(CSRF_HEADER, csrf);
  }

  if (import.meta.env.DEV) {
    config.headers.set("ngrok-skip-browser-warning", "true");
  }
  return config;
});

// Re-exchange in flight — coalesces concurrent 401s to a single refresh +
// /v1/auth/session call.
//
// The token that feeds the re-exchange depends on AUTH_MODE:
//   cognito — refresh the Amplify session and exchange the fresh Cognito ID
//             token (the behaviour this app has always had).
//   oidc    — re-exchange the STORED issuer id_token. The Penn JWT is short;
//             the issuer token lives up to 1h, so a Penn-JWT 401 mid-session is
//             usually recoverable WITHOUT a full re-login. There is no issuer
//             refresh grant.
// The discriminated result lets the 401 handler tell a definitive identity
// rejection (svc-02 answered 401/403 -> full logout) from a missing token
// (-> local teardown only) and from a transient failure (-> leave the session
// intact and recoverable).
type ReExchangeResult =
  | { outcome: "refreshed"; token: string; user: AuthUser }
  | { outcome: "no-token" }
  | { outcome: "identity-rejected" }
  | { outcome: "transient" };

let pendingReExchange: Promise<ReExchangeResult> | null = null;

async function sourceTokenForReExchange(): Promise<string | null> {
  if (!OIDC_ENABLED) return refreshCognitoIdToken();
  const idToken = storedIssuerIdToken();
  // svc-02 answers an EXPIRED issuer id_token with the same generic 401 as a
  // forged one, which the handler below reads as "identity-rejected" and
  // answers by ending the ISSUER session — the 24h platform SSO, for every
  // app. Expiry is not a rejection: report "no token" instead, so the outcome
  // is the local teardown + /login. The 24h SSO cookie survives, so the next
  // sign-in completes at the issuer without a credential prompt. At BOOT that
  // no longer costs a click: session-boot answers this outcome with a
  // `prompt=none` attempt against that cookie (ISC-403) and returns the user to
  // the page they were on. On a mid-session 401 the handler below still ends at
  // /login, which does NOT auto-start on a no-intent arrival (OidcLoginPage).
  // Only a provably past `exp` is treated this way; anything else still goes
  // to svc-02.
  if (idToken && isIssuerIdTokenExpired(idToken)) return null;
  return idToken;
}

// Exported for the boot restore (session-boot.ts), which runs this same
// single-flight recovery when the cookie path yields nothing.
export async function attemptReExchange(): Promise<ReExchangeResult> {
  if (!pendingReExchange) {
    pendingReExchange = (async () => {
      try {
        const sourceToken = await sourceTokenForReExchange();
        if (!sourceToken) return { outcome: "no-token" };
        // Direct axios call to /v1/auth/session bypassing this instance —
        // we don't want our own request interceptor attaching the stale
        // Penn token, and we don't want recursion if the call 401s.
        const response = await axios.post(
          `${API_BASE_URL}/v1/auth/session`,
          // `appContext` is read on the platform-issuer path only, where one
          // OIDC client serves both surfaces. Without it svc-02 defaults to
          // "web", so an internal identity re-exchanging from the admin surface
          // is refused as wrong-app-for-account and the handler below ends the
          // whole platform session. Cognito tokens ignore it; keep that body bare.
          OIDC_ENABLED
            ? { idToken: sourceToken, appContext: webCommonHost().appContext }
            : { idToken: sourceToken },
          // withCredentials: the exchange rotates the cookie session (ADR-0036);
          // on a cross-origin API host the browser drops Set-Cookie without it.
          { timeout: 15_000, withCredentials: true },
        );
        const data = response.data?.data ?? response.data;
        const newToken = data?.accessToken as string | undefined;
        const user = data?.user;
        if (!newToken || !user) return { outcome: "transient" };
        // The rotated session's CSRF value, echoed on /refresh and /logout.
        if (typeof data?.csrfToken === "string") setCsrfToken(data.csrfToken);
        authStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        authStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
        return {
          outcome: "refreshed",
          token: newToken,
          user: user as AuthUser,
        };
      } catch (e) {
        const status = axios.isAxiosError(e) ? e.response?.status : undefined;
        if (status === 401 || status === 403) {
          return { outcome: "identity-rejected" };
        }
        return { outcome: "transient" };
      } finally {
        // Clear after the next tick so concurrent waiters all see the same
        // result before we reset.
        setTimeout(() => {
          pendingReExchange = null;
        }, 0);
      }
    })();
  }
  return pendingReExchange;
}

/**
 * Revoke the server-side cookie session (deletes the `auth_sessions` row and
 * clears `penn_rt` + `penn_csrf`). A direct axios call: the app instance's
 * interceptors would recurse into this very handler on a 401. Best-effort, like
 * NavBar's deliberate sign-out — a failure must not strand the teardown.
 */
async function revokeCookieSession(): Promise<void> {
  const csrf = readCsrfCookie();
  await axios
    .post(
      `${API_BASE_URL}/v1/auth/logout`,
      {},
      {
        timeout: 15_000,
        withCredentials: true,
        headers: csrf ? { [CSRF_HEADER]: csrf } : {},
      },
    )
    .catch(() => undefined);
}

function forceLocalLogout(): void {
  authStorage.removeItem(TOKEN_STORAGE_KEY);
  authStorage.removeItem(USER_STORAGE_KEY);
  // Only the oidc "identity-rejected" path uses this: svc-02 refused the
  // identity itself, so the other tabs must go too (ADR-0036 keeps the
  // broadcast for deliberate and server-side logouts, never local failures).
  broadcastLogout();
}

/** What a 401 on a business route came to. */
export type UnauthorizedRecovery =
  | { kind: "retry"; token: string }
  /** A full-page navigation (the issuer, or Sign in) is under way. */
  | { kind: "navigating" }
  | { kind: "failed" };

let pendingRecovery: Promise<UnauthorizedRecovery> | null = null;
let leavingPage = false;

/**
 * Recover this page's session after a 401 on a business route. Shared by every
 * Axios instance, so the admin console and the customer site recover the same
 * way.
 *
 * Single-flight: concurrent 401s share one recovery and one outcome. Sticky
 * once a navigation has started, so a later 401 cannot override it; without
 * both, a second 401 would find the silent attempt suppressed and send the
 * page to /login mid-navigation.
 */
export function recoverFromUnauthorized(): Promise<UnauthorizedRecovery> {
  if (leavingPage) return Promise.resolve({ kind: "navigating" });
  if (!pendingRecovery) {
    pendingRecovery = runRecovery().then((outcome) => {
      if (outcome.kind === "navigating") leavingPage = true;
      return outcome;
    });
    const clear = () => {
      setTimeout(() => {
        pendingRecovery = null;
      }, 0);
    };
    pendingRecovery.then(clear, clear);
  }
  return pendingRecovery;
}

async function runRecovery(): Promise<UnauthorizedRecovery> {
  // Recovery order (ADR-0036): the cookie session first (cheap, no identity
  // round trip, and it re-reads role + permissions server-side), then the
  // AUTH_MODE-specific re-exchange as the fallback. All single-flight, so a
  // burst of 401s produces one recovery attempt.
  const refreshed = AUTH_COOKIE_SESSION ? await refreshSession() : null;
  if (refreshed?.accessToken) {
    return { kind: "retry", token: refreshed.accessToken };
  }

  const result = await attemptReExchange();

  if (result.outcome === "refreshed") {
    return { kind: "retry", token: result.token };
  }

  if (!OIDC_ENABLED) {
    // Cognito: any non-refresh outcome is the pre-existing local logout.
    authStorage.removeItem(TOKEN_STORAGE_KEY);
    authStorage.removeItem(USER_STORAGE_KEY);

    // NOTE: `broadcastLogout()` is deliberately NOT called here.
    //
    // This branch is reached whenever recovery failed in THIS tab — which
    // includes entirely local causes: a throttled background tab, a dropped
    // network, a transient 5xx behind the 401. Broadcasting from here evicted
    // every other tab, including tabs holding a valid token and a working
    // Cognito session, and was the direct cause of "logged out several times
    // a day". A deliberate logout still broadcasts (see NavBar); an
    // unrecoverable 401 now only redirects the tab it happened in.
    if (
      typeof window !== "undefined" &&
      window.location.pathname !== "/login"
    ) {
      window.location.replace("/login");
    }
  } else if (result.outcome === "transient") {
    // Leave the user RECOVERABLE: keep both the Penn token and the issuer
    // token, do not broadcast, do not end the issuer session. The next
    // request retries the exchange (`_retried` is per-request-config only).
  } else if (result.outcome === "no-token") {
    // The issuer id_token has expired (or was never held). If it expired,
    // the platform SSO session may still be live: renew silently and come
    // back to this page rather than dropping the user on Sign in. Unsaved
    // page state is lost on that navigation, the accepted cost. The helper
    // refuses for a visitor holding no token, while a logout is in flight,
    // and while a previous silent attempt is suppressed, so it cannot loop.
    if (await beginSilentReauthIfExpired()) return { kind: "navigating" };
    // Nothing to renew: an anonymous visitor, a public page, or a tab that
    // never held a session. LOCAL teardown, then the pathname guard. Skip
    // while a logout navigation is already committed: endSession() clears
    // the issuer token, so a request that 401s in that window lands here
    // and would otherwise supersede the pending /logout.
    authStorage.removeItem(TOKEN_STORAGE_KEY);
    authStorage.removeItem(USER_STORAGE_KEY);
    if (
      typeof window !== "undefined" &&
      !isLoggingOut() &&
      window.location.pathname !== "/login"
    ) {
      window.location.assign("/login");
    }
  } else {
    // "identity-rejected": svc-02 affirmatively refused this identity
    // (deactivated / role revoked / wrong-app / not registered). End the
    // ISSUER session, not just ours: bouncing to /login would re-run
    // beginLogin() against the still-live SSO cookie, silently re-mint a
    // code, and 401 again — an unbounded loop for a revoked user.
    // endSession() also clears the stored issuer id_token, so
    // attemptReExchange() cannot resurrect it. This is a server-side
    // refusal, not a local failure, so evicting the other tabs is right.
    //
    // Same order as NavBar's deliberate sign-out: stop the refresh timer and
    // revoke the cookie session BEFORE the local teardown. Otherwise the
    // surviving `penn_rt` signs the refused identity straight back in on the
    // next boot refresh, and a timer tick can re-mint a token meanwhile.
    stopSessionRefresh();
    await revokeCookieSession();
    forceLocalLogout();
    if (typeof window !== "undefined") {
      endSession();
    }
  }
  return { kind: "failed" };
}

Api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url ?? "";
    const isAuthEndpoint =
      url.includes("/v1/auth/session") || url.includes("/v1/auth/refresh");
    const cfg = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;

    if (status === 401 && !isAuthEndpoint && cfg && !cfg._retried) {
      cfg._retried = true;
      const recovery = await recoverFromUnauthorized();
      if (recovery.kind === "retry") {
        cfg.headers?.set?.("Authorization", `Bearer ${recovery.token}`);
        return Api.request(cfg);
      }
      // The page is leaving; never settle, so no caller renders an error.
      if (recovery.kind === "navigating") return new Promise<never>(() => {});
    }

    // Surface the backend's human-readable message everywhere. The server
    // ships { error: { code, message } }; axios otherwise leaves
    // `error.message` as the useless "Request failed with status code NNN".
    // Rewriting it here means every React Query surface + any `catch (e) =>
    // e.message` shows the real message without touching each call site. The
    // structured envelope is still available on `error.response.data` for
    // code-specific branching (getApiErrorCode).
    const apiMessage = (error.response?.data as ErrorEnvelope | undefined)
      ?.error?.message;
    if (typeof apiMessage === "string" && apiMessage.trim().length > 0) {
      error.message = apiMessage.trim();
    }
    return Promise.reject(error);
  },
);

export default Api;
